import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Spinner,
  Text,
  useDisclosure,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  Icon,
  Input,
  Switch,
  SimpleGrid,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  ArrowLeft,
  Funnel,
  GearSix,
  Ticket,
  ShieldCheck,
  WarningCircle,
  ArrowSquareOut,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DropResult } from '@hello-pangea/dnd';
import {
  useKanban,
  IKanbanBoard,
  IKanbanColumn,
  IKanbanCard,
  IKanbanProject,
  IKanbanCampoDefinicao,
  IKanbanCampoValorInput,
  IKanbanResponse,
  IKanbanUserOption,
  IKanbanVinculo,
  KanbanPrioridade,
  IColunaPermMap,
} from '../../hooks/useKanban';
import { calculateRankBetween } from '../../utils/lexoRank';
import { useAuth } from '../../providers/auth';
import { KanbanBoard } from './KanbanBoard';
import { ordemGuardada } from './KanbanColumn';
import { KanbanCardModal } from './KanbanCardModal';
import FaixaTemplateDoEvento from '../../pages/eventos/components/contrato/FaixaTemplateDoEvento';
import { KanbanColumnModal } from './KanbanColumnModal';
import { KanbanAddCardModal } from './KanbanAddCardModal';
// eslint-disable-next-line import/extensions
import { KanbanFieldFilters } from './KanbanFieldFilters';
// eslint-disable-next-line import/extensions
import { KanbanFieldManager } from './KanbanFieldManager';
import DialogModal from '../DialogModal';
import { AutentiqueSignModal } from './AutentiqueSignModal';
import { resolverModuloDeOrigem } from './modulosDeOrigem';
import { useAutentique } from '../../hooks/useAutentique';
import { api } from '../../services/api';

interface KanbanBoardContentProps {
  projetoId: number;
  onBack?: () => void;
  /**
   * Força o board a ficar editável mesmo quando o projeto pertence a um módulo
   * (origem_modulo != null). Usado quando o board é renderizado DENTRO da tela
   * do próprio módulo (ex.: aba Kanban em /eventos) — nesse contexto a edição
   * é sempre permitida; o espelho read-only só se aplica ao /kanban geral.
   */
  forceEditable?: boolean;
  /** Esconde o header sticky (título do projeto + ações) — a tela hospedeira
   * já tem seu próprio cabeçalho. */
  hideHeader?: boolean;
}

export const KanbanBoardContent = ({
  projetoId,
  onBack,
  forceEditable = false,
  hideHeader = false,
}: KanbanBoardContentProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuth();
  const {
    getProjects,
    getBoard,
    createColumn,
    updateColumn,
    moveColumn,
    archiveColumn,
    createCard,
    updateCard,
    moveCard,
    archiveCard,
    getCampoDefinicoes,
    getUsersByDepartamentos,
    getColunaPermissoes,
  } = useKanban();

  // Modais
  const {
    isOpen: isCardModalOpen,
    onOpen: onCardModalOpen,
    onClose: onCardModalClose,
  } = useDisclosure();
  const {
    isOpen: isColumnModalOpen,
    onOpen: onColumnModalOpen,
    onClose: onColumnModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const {
    isOpen: isAddCardModalOpen,
    onOpen: onAddCardModalOpen,
    onClose: onAddCardModalClose,
  } = useDisclosure();
  const {
    isOpen: isFieldManagerOpen,
    onOpen: onFieldManagerOpen,
    onClose: onFieldManagerClose,
  } = useDisclosure();

  const {
    isOpen: isSignModalOpen,
    onOpen: onSignModalOpen,
    onClose: onSignModalClose,
  } = useDisclosure();

  // Estado
  const [selectedCard, setSelectedCard] = useState<IKanbanCard | null>(null);
  const [selectedCardColumn, setSelectedCardColumn] = useState<string | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<IKanbanColumn | null>(null);
  const [columnToArchive, setColumnToArchive] = useState<number | null>(null);
  const [columnForNewCard, setColumnForNewCard] = useState<IKanbanColumn | null>(null);
  const [optimisticColumns, setOptimisticColumns] = useState<IKanbanColumn[] | null>(null);
  const [filtroCampos, setFiltroCampos] = useState<Record<string, unknown>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState<{
    cardId: number;
    destColumnId: number;
    prevRank: string | null;
    nextRank: string | null;
    card: IKanbanCard;
  } | null>(null);
  const [pendingDocDialog, setPendingDocDialog] = useState<{
    cardId: number;
    destColumnId: number;
    prevRank: string | null;
    nextRank: string | null;
    card: any;
  } | null>(null);
  const [pendingRevisao, setPendingRevisao] = useState<{
    cardId: number;
    destColumnId: number;
    prevRank: string | null;
    nextRank: string | null;
    card: IKanbanCard;
  } | null>(null);
  const [pendingNfEmitida, setPendingNfEmitida] = useState<{
    cardId: number;
    destColumnId: number;
    prevRank: string | null;
    nextRank: string | null;
  } | null>(null);
  const [nfNumero, setNfNumero] = useState('');
  const [nfValor, setNfValor] = useState('');
  const [nfTemRetencao, setNfTemRetencao] = useState(false);
  const [nfRetencoes, setNfRetencoes] = useState({
    iss: '',
    ir: '',
    csll: '',
    cofins: '',
    pis: '',
    inss: '',
  });
  const [nfSending, setNfSending] = useState(false);

  const [confirmandoRevisao, setConfirmandoRevisao] = useState(false);
  const revisaoPedida = useRef(0);

  const { cancelarPending, syncPendentes, getStatus } = useAutentique();

  const grupoId = Number(user?.grupo_id);
  const isAdminKanban = [1, 3, 5].includes(grupoId); // Admin, Diretoria, Desenvolvimento
  const isGestor = grupoId === 4;
  const canManage = isAdminKanban || isGestor;

  const numericProjectId = projetoId;

  // Query de projetos
  const { data: projectsData } = useQuery<IKanbanResponse<IKanbanProject[]>>({
    queryKey: ['kanban-projects'],
    queryFn: getProjects,
    staleTime: 30_000,
  });

  const projects = projectsData?.data || [];
  const currentProject = projects.find(p => p.id === numericProjectId);
  const projectTitle = currentProject?.nome || currentProject?.titulo || 'Projeto';

  // Query do board. Só os campos personalizados (id numérico) vão ao servidor e à chave: responsável e
  // expiração filtram aqui (applyLocalFilters), sem buscar o quadro de novo nem cair no carregando.
  const apiFilters = Object.fromEntries(
    Object.entries(filtroCampos).filter(
      ([k, v]) => /^\d+$/.test(k) && v !== null && v !== undefined && v !== '',
    ),
  );
  const { data, isLoading, error } = useQuery<IKanbanResponse<IKanbanBoard>>({
    queryKey: ['kanban-board', numericProjectId, apiFilters],
    queryFn: () => getBoard(numericProjectId, false, apiFilters),
    enabled: !!numericProjectId,
  });

  // Query de campos personalizaveis
  const { data: camposData } = useQuery<IKanbanResponse<IKanbanCampoDefinicao[]>>({
    queryKey: ['kanban-campos', numericProjectId],
    queryFn: () => getCampoDefinicoes(numericProjectId!),
    enabled: !!numericProjectId,
    staleTime: 30_000,
  });

  const campos = camposData?.data || [];
  const deptIds = currentProject?.departamentos?.map(d => d.id) || [];

  // Query de usuarios do projeto
  const { data: usersData } = useQuery<IKanbanResponse<IKanbanUserOption[]>>({
    queryKey: ['kanban-users', deptIds],
    queryFn: () => getUsersByDepartamentos(deptIds),
    enabled: !!numericProjectId && deptIds.length > 0,
    staleTime: 30_000,
  });
  const usersFromApi = usersData?.data || [];

  const isEventProject = currentProject?.evento_id != null;

  // Quadro de módulo (task #757): aberto a partir do /kanban geral, é um espelho
  // READ-ONLY do board do módulo (Estatutos/Eventos). A edição acontece na tela
  // do módulo original — por isso o board fica somente-leitura e oferece o botão
  // "Abrir no módulo". Quando `forceEditable` é true (board renderizado DENTRO
  // da tela do próprio módulo), a edição fica sempre liberada.
  const origemModulo = currentProject?.origem_modulo ?? null;
  const isModuleMirror = !forceEditable && origemModulo != null;
  // Rota do módulo de origem p/ o botão "Abrir no módulo". A decodificação de
  // `origem_modulo` mora no registry (`modulosDeOrigem.ts`) — aqui fica só o
  // que é DESTA tela: o evento específico tem página própria (`/eventos/:id`).
  const moduloHref = (() => {
    const modulo = resolverModuloDeOrigem(origemModulo);
    if (!modulo?.rota) return null; // módulo sem tela ainda → sem botão
    if (origemModulo === 'eventos' && currentProject?.evento_id) {
      return `${modulo.rota}/${currentProject.evento_id}`;
    }
    return modulo.rota;
  })();

  // Validação inicial ao abrir um board de evento: sincroniza documentos
  // Autentique pendentes (recupera webhook perdido enquanto o backend esteve
  // fora/sem WS). Best-effort e não-bloqueante — o board já renderiza com o
  // estado do banco; se algo mudou, invalida pra refletir.
  useEffect(() => {
    if (!isEventProject) return undefined;
    let active = true;
    syncPendentes()
      .then((res: any) => {
        if (!active) return;
        if ((res?.data?.atualizados?.length ?? 0) > 0) {
          queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
        }
      })
      .catch(() => {
        // Silencioso: recuperação oportunista, não pode atrapalhar o board.
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventProject, numericProjectId]);

  const { data: colunaPermsData } = useQuery<IKanbanResponse<IColunaPermMap>>({
    queryKey: ['kanban-coluna-perms'],
    queryFn: getColunaPermissoes,
    enabled: isEventProject,
    staleTime: 30_000,
  });
  const colunaPerms = colunaPermsData?.data || {};

  const columns = data?.data?.columns || [];

  // Extrair responsaveis unicos dos cards do board + usuarios da API
  const projectUsers = React.useMemo(() => {
    const userMap = new Map<number, IKanbanUserOption>();
    // Usuarios da API de departamentos
    usersFromApi.forEach(u => userMap.set(u.id, u));
    // Usuarios dos cards (podem estar em departamentos diferentes)
    columns.forEach(col =>
      (col.cards || []).forEach(card =>
        (card.usuarios || []).forEach(u => {
          if (!userMap.has(u.id)) {
            userMap.set(u.id, { id: u.id, nome: u.nome || '', email: '' });
          }
        }),
      ),
    );
    return Array.from(userMap.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [usersFromApi, columns]);

  // Aplicar filtros locais (responsavel, expiracao)
  const applyLocalFilters = (cols: typeof columns) => {
    const responsavelId = filtroCampos.responsavel ? Number(filtroCampos.responsavel) : null;
    const expiraDe = filtroCampos.expiraDe ? String(filtroCampos.expiraDe) : null;
    const expiraAte = filtroCampos.expiraAte ? String(filtroCampos.expiraAte) : null;

    if (!responsavelId && !expiraDe && !expiraAte) return cols;

    return cols.map(col => ({
      ...col,
      cards: (col.cards || []).filter(card => {
        if (responsavelId && !card.usuarios.some(u => u.id === responsavelId)) return false;
        if (expiraDe || expiraAte) {
          if (!card.expira_em) return false;
          const exp = card.expira_em.split('T')[0];
          if (expiraDe && exp < expiraDe) return false;
          if (expiraAte && exp > expiraAte) return false;
        }
        return true;
      }),
    }));
  };

  const filteredColumns = applyLocalFilters(columns);
  const displayColumns = optimisticColumns || filteredColumns;

  const activeFilterCount = Object.values(filtroCampos).filter(
    v => v !== null && v !== undefined && v !== '',
  ).length;

  // Card Mutations
  const createCardMutation = useMutation({
    mutationFn: createCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      toast.success('Card criado com sucesso!');
      onAddCardModalClose();
    },
    onError: () => {
      toast.error('Erro ao criar card');
      onAddCardModalClose();
    },
  });

  const updateCardMutation = useMutation({
    mutationFn: updateCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      toast.success('Card atualizado!');
    },
    onError: () => toast.error('Erro ao atualizar card'),
  });

  const moveCardMutation = useMutation({
    mutationFn: moveCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      // Sincronizar com eventos apenas se for projeto de evento
      if (isEventProject) {
        queryClient.invalidateQueries({ queryKey: ['evento-contratos'] });
        queryClient.invalidateQueries({ queryKey: ['evento-detalhe'] });
        queryClient.invalidateQueries({ queryKey: ['eventos-alertas'] });
      }
    },
    onError: () => {
      toast.error('Erro ao mover card');
      setOptimisticColumns(null);
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
    },
  });

  const archiveCardMutation = useMutation({
    mutationFn: archiveCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      // contagem do "Arquivados" do quadro do evento
      queryClient.invalidateQueries({ queryKey: ['evento-contratos-arquivados'] });
      toast.success('Card arquivado!');
    },
    onError: () => toast.error('Erro ao arquivar card'),
  });

  // Column Mutations
  const createColumnMutation = useMutation({
    mutationFn: createColumn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      toast.success('Coluna criada!');
    },
    onError: () => toast.error('Erro ao criar coluna'),
  });

  const updateColumnMutation = useMutation({
    mutationFn: updateColumn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      toast.success('Coluna atualizada!');
    },
    onError: () => toast.error('Erro ao atualizar coluna'),
  });

  const moveColumnMutation = useMutation({
    mutationFn: moveColumn,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] }),
    onError: () => {
      toast.error('Erro ao mover coluna');
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
    },
  });

  const archiveColumnMutation = useMutation({
    mutationFn: archiveColumn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
      toast.success('Coluna arquivada!');
    },
    onError: () => toast.error('Erro ao arquivar coluna'),
  });

  // Card Handlers
  const handleCardClick = (card: IKanbanCard) => {
    // No espelho de módulo o card ABRE o modal em modo READ-ONLY (visualização
    // completa, sem editar/criar/mover — a edição é na tela do módulo de origem).
    // O KanbanCardModal recebe isReadOnly={isModuleMirror} e esconde as mutações.
    setSelectedCard(card);
    const col = displayColumns.find(c => c.cards.some(cc => cc.id === card.id));
    setSelectedCardColumn(col?.titulo || null);
    onCardModalOpen();
  };
  // Mesma função em todo render: com uma nova, o memo do KanbanCard não segura e abrir/fechar o
  // modal redesenha o quadro inteiro (156 cards no evento 93, ~250 ms por clique).
  const cardClickRef = useRef(handleCardClick);
  cardClickRef.current = handleCardClick;
  const abrirCard = useCallback((card: IKanbanCard) => cardClickRef.current(card), []);

  const handleAddCardClick = (column: IKanbanColumn) => {
    setColumnForNewCard(column);
    onAddCardModalOpen();
  };

  const handleCreateCard = (
    titulo: string,
    descricao: string,
    userIds?: number[],
    prioridade?: KanbanPrioridade,
    expiraEm?: string,
    campoValores?: IKanbanCampoValorInput[],
    contratoData?: any,
    vinculos?: IKanbanVinculo[],
  ) => {
    if (!columnForNewCard) return;
    const lastCard = columnForNewCard.cards?.[columnForNewCard.cards.length - 1];

    createCardMutation.mutate({
      titulo,
      descricao,
      column_id: columnForNewCard.id,
      // `vinculos` carrega responsáveis + supervisores (com papel); `user_ids`
      // segue p/ compat (responsáveis).
      user_ids: userIds,
      vinculos,
      prioridade: prioridade || 'media',
      expira_em: expiraEm || null,
      prev_rank: lastCard?.rank || null,
      next_rank: null,
      campo_valores: campoValores,
      ...contratoData,
    });
  };

  const handleSaveCard = (
    id: number,
    titulo: string,
    descricao: string,
    prioridade: KanbanPrioridade,
    expiraEm?: string | null,
    campoValores?: IKanbanCampoValorInput[],
    userIds?: number[],
    vinculos?: IKanbanVinculo[],
  ) => {
    updateCardMutation.mutate({
      id,
      titulo,
      descricao,
      prioridade,
      expira_em: expiraEm,
      campo_valores: campoValores,
      user_ids: userIds,
      vinculos,
    });
  };

  const handleArchiveCard = (id: number) => archiveCardMutation.mutate(id);

  // Column Handlers
  const handleEditColumn = (column: IKanbanColumn) => {
    setSelectedColumn(column);
    onColumnModalOpen();
  };

  const handleArchiveColumn = (columnId: number) => {
    setColumnToArchive(columnId);
    onDeleteDialogOpen();
  };

  const confirmArchiveColumn = () => {
    if (columnToArchive) {
      archiveColumnMutation.mutate(columnToArchive);
      setColumnToArchive(null);
    }
    onDeleteDialogClose();
  };

  const handleSaveColumn = (titulo: string, projetoIdArg?: number, columnId?: number) => {
    if (columnId) {
      updateColumnMutation.mutate({ id: columnId, titulo, projeto_id: projetoIdArg });
    } else {
      const lastColumn = columns[columns.length - 1];
      createColumnMutation.mutate({
        titulo,
        projeto_id: numericProjectId,
        prev_rank: lastColumn?.rank || null,
        next_rank: null,
      });
    }
    setSelectedColumn(null);
  };

  const handleOpenNewColumnModal = () => {
    setSelectedColumn(null);
    onColumnModalOpen();
  };

  const clearOptimistic = () => setOptimisticColumns(null);

  const handleDragEnd = (result: DropResult) => {
    // Espelho de módulo é read-only: ignora qualquer movimentação (task #757).
    if (isModuleMirror) return;
    const { source, destination, type, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index)
      return;

    if (type === 'column') {
      const columnId = parseInt(draggableId.replace('column-', ''), 10);
      const filtered = columns.filter(c => c.id !== columnId);
      let prevRank: string | null = null;
      let nextRank: string | null = null;

      if (destination.index === 0) {
        nextRank = filtered[0]?.rank || null;
      } else if (destination.index >= filtered.length) {
        prevRank = filtered[filtered.length - 1]?.rank || null;
      } else {
        prevRank = filtered[destination.index - 1]?.rank || null;
        nextRank = filtered[destination.index]?.rank || null;
      }

      const newRank = calculateRankBetween(prevRank, nextRank);
      const movedColumn = columns.find(c => c.id === columnId);
      if (movedColumn) {
        const newColumns = columns.filter(c => c.id !== columnId);
        newColumns.splice(destination.index, 0, { ...movedColumn, rank: newRank });
        setOptimisticColumns(newColumns);
      }

      moveColumnMutation.mutate(
        { id: columnId, prev_rank: prevRank, next_rank: nextRank },
        { onSettled: clearOptimistic },
      );
    } else if (type === 'card') {
      const cardId = parseInt(draggableId.replace('card-', ''), 10);
      const sourceColumnId = parseInt(source.droppableId.replace('column-', ''), 10);
      const destColumnId = parseInt(destination.droppableId.replace('column-', ''), 10);

      const sourceColumn = columns.find(c => c.id === sourceColumnId);
      const destColumn = columns.find(c => c.id === destColumnId);
      if (!sourceColumn || !destColumn) return;

      // Bloquear retrocesso em projetos de evento sem permissão
      if (isEventProject && sourceColumnId !== destColumnId) {
        // Usar índice das colunas no board como ordem (já vem ordenado do backend)
        const colOrdem: Record<string, number> = {};
        columns.forEach((col, idx) => {
          colOrdem[col.titulo] = idx;
        });
        const srcOrdem = colOrdem[sourceColumn.titulo];
        const dstOrdem = colOrdem[destColumn.titulo];
        if (srcOrdem !== undefined && dstOrdem !== undefined) {
          const destPerms = colunaPerms[destColumn.titulo];

          // Bloquear avanço sem permissão na coluna destino
          if (dstOrdem > srcOrdem && destPerms && !destPerms.avancar_para) {
            toast.error(`Seu grupo não tem permissão para avançar para "${destColumn.titulo}".`);
            return;
          }
          // Bloquear retrocesso sem permissão na coluna destino
          if (dstOrdem < srcOrdem && destPerms && !destPerms.retroceder_para) {
            toast.error(`Seu grupo não tem permissão para retroceder para "${destColumn.titulo}".`);
            return;
          }
          // Bloquear pular colunas sem permissão
          if (
            Math.abs(dstOrdem - srcOrdem) > 1 &&
            !hasPermission('/kanban/eventos', 'pular_coluna')
          ) {
            toast.error('Não é permitido pular colunas no fluxo de eventos.');
            return;
          }
        }
      }

      let prevRank: string | null = null;
      let nextRank: string | null = null;

      if (sourceColumnId === destColumnId) {
        // Coluna ordenada (ex.: por chegada): a tela reordena de novo e o arraste so' mexeria na
        // ordem manual escondida, calculada com o indice da ordem exibida.
        if (isEventProject && ordemGuardada(sourceColumnId) !== 'rank') {
          toast.info('Coluna ordenada: volte para "Manual" para reposicionar o card.');
          return;
        }
        const cardsWithoutMoved = (sourceColumn.cards || []).filter(c => c.id !== cardId);
        const adjustedIndex =
          source.index < destination.index ? destination.index - 1 : destination.index;

        if (adjustedIndex === 0) {
          nextRank = cardsWithoutMoved[0]?.rank || null;
        } else if (adjustedIndex >= cardsWithoutMoved.length) {
          prevRank = cardsWithoutMoved[cardsWithoutMoved.length - 1]?.rank || null;
        } else {
          prevRank = cardsWithoutMoved[adjustedIndex - 1]?.rank || null;
          nextRank = cardsWithoutMoved[adjustedIndex]?.rank || null;
        }
      } else {
        const destCards = destColumn.cards || [];
        if (destination.index === 0) {
          nextRank = destCards[0]?.rank || null;
        } else if (destination.index >= destCards.length) {
          prevRank = destCards[destCards.length - 1]?.rank || null;
        } else {
          prevRank = destCards[destination.index - 1]?.rank || null;
          nextRank = destCards[destination.index]?.rank || null;
        }
      }

      const newRank = calculateRankBetween(prevRank, nextRank);
      const card = sourceColumn.cards?.find(c => c.id === cardId);

      if (card) {
        const updatedCard = { ...card, rank: newRank };
        const spliceIndex =
          sourceColumnId === destColumnId && source.index < destination.index
            ? destination.index - 1
            : destination.index;

        const newColumns = columns.map(col => {
          if (col.id === sourceColumnId && sourceColumnId !== destColumnId) {
            return { ...col, cards: (col.cards || []).filter(c => c.id !== cardId) };
          }
          if (col.id === destColumnId) {
            const newCards = (col.cards || []).filter(c => c.id !== cardId);
            newCards.splice(spliceIndex, 0, updatedCard);
            return { ...col, cards: newCards };
          }
          return col;
        });
        setOptimisticColumns(newColumns);
      }

      // Interceptar: se destino é "ENVIADO P/ ASS." e projeto é de evento, pedir confirmação de revisão
      if (
        isEventProject &&
        destColumn.titulo === 'ENVIADO P/ ASS.' &&
        sourceColumn.titulo !== 'ENVIADO P/ ASS.'
      ) {
        const movedCard = sourceColumn.cards?.find(c => c.id === cardId);
        if (movedCard) {
          setOptimisticColumns(null);
          setPendingRevisao({ cardId, destColumnId, prevRank, nextRank, card: movedCard });
          return;
        }
      }

      // Interceptar: se destino é "NF EMITIDA" e projeto é de evento, pedir dados da NF
      if (
        isEventProject &&
        destColumn.titulo === 'NF EMITIDA' &&
        sourceColumn.titulo !== 'NF EMITIDA'
      ) {
        setOptimisticColumns(null);
        setNfNumero('');
        setNfValor('');
        setNfTemRetencao(false);
        setNfRetencoes({ iss: '', ir: '', csll: '', cofins: '', pis: '', inss: '' });
        setPendingNfEmitida({ cardId, destColumnId, prevRank, nextRank });
        return;
      }

      moveCardMutation.mutate(
        { id: cardId, column_id: destColumnId, prev_rank: prevRank, next_rank: nextRank },
        { onSettled: clearOptimistic },
      );
    }
  };

  const handleAutentiqueSuccess = () => {
    if (pendingMoveData) {
      moveCardMutation.mutate(
        {
          id: pendingMoveData.cardId,
          column_id: pendingMoveData.destColumnId,
          prev_rank: pendingMoveData.prevRank,
          next_rank: pendingMoveData.nextRank,
        },
        { onSettled: clearOptimistic },
      );
      setPendingMoveData(null);
    }
    onSignModalClose();
  };

  const handlePendingMoveOnly = () => {
    if (!pendingDocDialog) return;
    moveCardMutation.mutate(
      {
        id: pendingDocDialog.cardId,
        column_id: pendingDocDialog.destColumnId,
        prev_rank: pendingDocDialog.prevRank,
        next_rank: pendingDocDialog.nextRank,
      },
      { onSettled: clearOptimistic },
    );
    setPendingDocDialog(null);
  };

  const handlePendingResend = async () => {
    if (!pendingDocDialog) return;
    try {
      await cancelarPending(pendingDocDialog.cardId);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erro ao cancelar documento anterior');
      setPendingDocDialog(null);
      return;
    }
    setPendingMoveData({
      cardId: pendingDocDialog.cardId,
      destColumnId: pendingDocDialog.destColumnId,
      prevRank: pendingDocDialog.prevRank,
      nextRank: pendingDocDialog.nextRank,
      card: pendingDocDialog.card,
    });
    setPendingDocDialog(null);
    onSignModalOpen();
  };

  const fecharRevisao = () => {
    revisaoPedida.current += 1; // a consulta em curso, se houver, é descartada
    setConfirmandoRevisao(false);
    setPendingRevisao(null);
  };

  const handleRevisaoConfirmada = async () => {
    if (!pendingRevisao || confirmandoRevisao) return;
    revisaoPedida.current += 1;
    const estaRevisao = revisaoPedida.current;
    setConfirmandoRevisao(true);
    const { card } = pendingRevisao;
    // O quadro pode estar velho (outra aba, envio de outra pessoa): reenviar o mesmo anexo dá erro.
    // O banco diz se já há documento pendente; sem resposta, fica o que o quadro sabe.
    // Falha no registro da revisão não impede o fluxo.
    const [, status] = await Promise.allSettled([
      api.post(`/api/v1/kanban/cards/${pendingRevisao.cardId}/confirmar-revisao/`),
      getStatus(pendingRevisao.cardId),
    ]);
    if (estaRevisao !== revisaoPedida.current) return; // fechou durante a consulta
    const temPendente =
      status.status === 'fulfilled'
        ? (status.value?.data ?? []).some(doc => doc.status === 'pending')
        : !!card.has_pending_autentique_doc;
    if (temPendente !== !!card.has_pending_autentique_doc) {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
    }
    setConfirmandoRevisao(false);

    if (temPendente) {
      setPendingDocDialog({
        cardId: pendingRevisao.cardId,
        destColumnId: pendingRevisao.destColumnId,
        prevRank: pendingRevisao.prevRank,
        nextRank: pendingRevisao.nextRank,
        card,
      });
    } else {
      setPendingMoveData({
        cardId: pendingRevisao.cardId,
        destColumnId: pendingRevisao.destColumnId,
        prevRank: pendingRevisao.prevRank,
        nextRank: pendingRevisao.nextRank,
        card,
      });
      onSignModalOpen();
    }
    setPendingRevisao(null);
  };

  const handleNfEmitidaConfirm = async () => {
    if (!pendingNfEmitida || !nfNumero.trim()) return;
    setNfSending(true);
    try {
      const parseVal = (v: string) => parseFloat(v.replace(/\./g, '').replace(',', '.')) || 0;
      await api.post(`/api/v1/kanban/cards/${pendingNfEmitida.cardId}/nf-emitida/`, {
        nf_numero: nfNumero.trim(),
        nf_valor: parseVal(nfValor),
        nf_retencao_iss: nfTemRetencao ? parseVal(nfRetencoes.iss) : 0,
        nf_retencao_ir: nfTemRetencao ? parseVal(nfRetencoes.ir) : 0,
        nf_retencao_csll: nfTemRetencao ? parseVal(nfRetencoes.csll) : 0,
        nf_retencao_cofins: nfTemRetencao ? parseVal(nfRetencoes.cofins) : 0,
        nf_retencao_pis: nfTemRetencao ? parseVal(nfRetencoes.pis) : 0,
        nf_retencao_inss: nfTemRetencao ? parseVal(nfRetencoes.inss) : 0,
      });
      moveCardMutation.mutate(
        {
          id: pendingNfEmitida.cardId,
          column_id: pendingNfEmitida.destColumnId,
          prev_rank: pendingNfEmitida.prevRank,
          next_rank: pendingNfEmitida.nextRank,
        },
        {
          onSettled: () =>
            queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] }),
        },
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar NF');
    } finally {
      setNfSending(false);
      setPendingNfEmitida(null);
    }
  };

  const handleSignModalClose = () => {
    onSignModalClose();
    setPendingMoveData(null);
    queryClient.invalidateQueries({ queryKey: ['kanban-board', numericProjectId] });
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/kanban');
    }
  };

  if (!numericProjectId) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <Text color="red.500">Projeto nao encontrado</Text>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="400px" flexDirection="column" gap={4}>
        <Text color="red.500">Erro ao carregar o board</Text>
        <Button onClick={handleBack} variant="outline">
          Voltar para projetos
        </Button>
      </Flex>
    );
  }

  return (
    <Box bg="white" minH={hideHeader ? undefined : '100vh'}>
      {/* Header */}
      {!hideHeader && (
        <Box
          px={{ base: 4, md: 8 }}
          pt={5}
          pb={4}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
            <Flex alignItems="center" gap={3}>
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} weight="bold" />}
                variant="ghost"
                borderRadius="full"
                size="sm"
                onClick={handleBack}
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
              />
              <Box>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                >
                  Projeto
                </Text>
                <Heading
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="700"
                  color="gray.900"
                  letterSpacing="-0.02em"
                >
                  {projectTitle}
                </Heading>
                {currentProject?.departamentos && currentProject.departamentos.length > 0 && (
                  <Flex gap={1} mt={1} flexWrap="wrap">
                    {currentProject.departamentos.map(d => (
                      <Text
                        key={d.id}
                        fontSize="10px"
                        color="gray.500"
                        bg="gray.100"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontWeight="500"
                      >
                        {d.nome}
                      </Text>
                    ))}
                  </Flex>
                )}
              </Box>
            </Flex>

            <Flex alignItems="center" gap={3}>
              {/* Quadro de módulo (task #757): espelho read-only → botão que leva
                  à tela do módulo original (Estatutos/Eventos). */}
              {isModuleMirror && moduloHref && (
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  borderRadius="full"
                  fontWeight="500"
                  fontSize="11px"
                  leftIcon={
                    origemModulo === 'eventos' ? <Ticket size={12} /> : <ArrowSquareOut size={12} />
                  }
                  onClick={() => navigate(moduloHref)}
                  _hover={{ transform: 'translateY(-1px)' }}
                  transition="all 0.15s ease"
                >
                  {origemModulo === 'eventos' ? 'Ver Evento' : 'Abrir no módulo'}
                </Button>
              )}
              {/* Filter toggle */}
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Funnel size={15} weight={activeFilterCount > 0 ? 'fill' : 'bold'} />}
                borderRadius="full"
                color={activeFilterCount > 0 ? 'blue.600' : 'gray.500'}
                bg={activeFilterCount > 0 ? 'blue.50' : 'transparent'}
                _hover={{ bg: activeFilterCount > 0 ? 'blue.100' : 'gray.100' }}
                onClick={() => setShowFilters(!showFilters)}
                fontSize="13px"
                fontWeight="500"
              >
                Filtros
                {activeFilterCount > 0 && (
                  <Flex
                    ml={1.5}
                    bg="blue.500"
                    color="white"
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {activeFilterCount}
                  </Flex>
                )}
              </Button>

              {isEventProject && isAdminKanban && (
                <Tooltip label="Permissões por coluna" fontSize="xs">
                  <IconButton
                    aria-label="Permissões por coluna"
                    icon={<ShieldCheck size={17} weight="bold" />}
                    variant="ghost"
                    size="sm"
                    borderRadius="full"
                    color="gray.400"
                    _hover={{ bg: 'gray.100', color: 'gray.600' }}
                    onClick={() => navigate('/configuracoes/permissoes?aba=kanban')}
                  />
                </Tooltip>
              )}
              {canManage && (
                <IconButton
                  aria-label="Gerenciar campos"
                  icon={<GearSix size={17} weight="bold" />}
                  variant="ghost"
                  size="sm"
                  borderRadius="full"
                  color="gray.400"
                  _hover={{ bg: 'gray.100', color: 'gray.600' }}
                  onClick={onFieldManagerOpen}
                />
              )}
            </Flex>
          </Flex>

          {/* Inline Filters */}
          {showFilters && (
            <KanbanFieldFilters
              campos={campos}
              filtros={filtroCampos}
              usuarios={projectUsers}
              onChange={setFiltroCampos}
            />
          )}
        </Box>
      )}

      {/* Board */}
      <Box
        px={{ base: 4, md: 6 }}
        py={5}
        bg="gray.50"
        minH={hideHeader ? undefined : 'calc(100vh - 100px)'}
      >
        {displayColumns.length === 0 ? (
          <Flex justify="center" align="center" minH="50vh" flexDirection="column" gap={5}>
            <Flex
              w="56px"
              h="56px"
              borderRadius="16px"
              bg="gray.100"
              align="center"
              justify="center"
            >
              <Plus size={24} color="#9CA3AF" weight="light" />
            </Flex>
            <Box textAlign="center">
              <Text fontSize="md" fontWeight="600" color="gray.700" mb={1}>
                Nenhuma coluna ainda
              </Text>
              <Text color="gray.400" fontSize="sm">
                Crie sua primeira coluna para comecar
              </Text>
            </Box>
            {canManage && (
              <Button
                leftIcon={<Plus size={15} weight="bold" />}
                bg="gray.900"
                color="white"
                size="sm"
                borderRadius="full"
                px={5}
                fontWeight="500"
                _hover={{ bg: 'gray.800' }}
                onClick={handleOpenNewColumnModal}
              >
                Criar primeira coluna
              </Button>
            )}
          </Flex>
        ) : (
          <KanbanBoard
            columns={displayColumns}
            canManage={canManage}
            colunaPerms={isEventProject ? colunaPerms : undefined}
            canEditProtected={hasPermission('/kanban/eventos', 'editar_coluna')}
            isReadOnly={isModuleMirror}
            destacarRecentes={isEventProject}
            topoDaColuna={
              isEventProject && !isModuleMirror && currentProject?.evento_id
                ? col =>
                    col.titulo === 'CONTRATO' ? (
                      <FaixaTemplateDoEvento eventoId={currentProject.evento_id as number} />
                    ) : null
                : undefined
            }
            onCardClick={abrirCard}
            onAddCardClick={handleAddCardClick}
            onEditColumn={handleEditColumn}
            onArchiveColumn={handleArchiveColumn}
            onDragEnd={handleDragEnd}
            onAddColumn={handleOpenNewColumnModal}
          />
        )}
      </Box>

      <KanbanCardModal
        isOpen={isCardModalOpen}
        onClose={onCardModalClose}
        card={selectedCard}
        campos={campos}
        departamentoIds={currentProject?.departamentos?.map(d => d.id)}
        onSave={handleSaveCard}
        onArchive={handleArchiveCard}
        columnTitle={selectedCardColumn}
        isEventProject={isEventProject}
        // Espelho de módulo: abre em modo só-leitura (esconde editar/salvar/mover/
        // arquivar/comentar/anexar/contrato). A edição é na tela do módulo de origem.
        isReadOnly={isModuleMirror}
        // forceEditable (aba Kanban dentro do próprio módulo) só libera de fato
        // editar/arquivar sem vínculo ao card se o usuário tiver a permissão
        // "editar_card" da tela `kanban_eventos` (RBAC geral, configurável por
        // grupo/usuário em /configuracoes) — alinhado ao backend
        // (verificar_permissao_card/_modulo_libera_equipe_interna).
        allowAnyEditor={forceEditable && hasPermission('/kanban/eventos', 'editar_card')}
      />

      <KanbanColumnModal
        isOpen={isColumnModalOpen}
        onClose={onColumnModalClose}
        column={selectedColumn}
        onSave={handleSaveColumn}
      />

      <KanbanAddCardModal
        isOpen={isAddCardModalOpen}
        onClose={onAddCardModalClose}
        columnId={columnForNewCard?.id || 0}
        columnTitle={columnForNewCard?.titulo || ''}
        departamentoIds={currentProject?.departamentos?.map(d => d.id)}
        campos={campos}
        isEventProject={isEventProject}
        onSave={handleCreateCard}
      />

      {numericProjectId && (
        <KanbanFieldManager
          isOpen={isFieldManagerOpen}
          onClose={onFieldManagerClose}
          projetoId={numericProjectId}
        />
      )}

      <DialogModal
        isOpen={isDeleteDialogOpen}
        status="warning"
        title="Arquivar coluna"
        description="Tem certeza que deseja arquivar esta coluna? Os cards dentro dela tambem serao arquivados."
        onConfirm={confirmArchiveColumn}
        onCancel={onDeleteDialogClose}
        confirmLabel="Arquivar"
        cancelLabel="Cancelar"
      />
      <AutentiqueSignModal
        isOpen={isSignModalOpen}
        onClose={handleSignModalClose}
        cardId={pendingMoveData?.cardId || 0}
        cardTitle={pendingMoveData?.card?.titulo || ''}
        hasAnexos={(pendingMoveData?.card?.anexos_count || 0) > 0}
        onSuccess={handleAutentiqueSuccess}
        sugerirAssinantes={isEventProject}
      />

      {/* Modal de confirmação de revisão */}
      <Modal isOpen={!!pendingRevisao} onClose={fecharRevisao} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="xl" bg="gray.900">
          <ModalHeader fontSize="md" color="white">
            Confirmar revisao do documento
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Flex align="center" gap={2}>
                <Icon as={ShieldCheck} color="blue.400" boxSize={5} />
                <Text fontSize="sm" color="gray.300">
                  O documento do contrato foi revisado e esta pronto para envio?
                </Text>
              </Flex>
              <Text fontSize="xs" color="gray.500">
                Ao confirmar, o documento sera enviado para assinatura digital. Esta acao ficara
                registrada no historico do card.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" color="gray.400" onClick={fecharRevisao}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleRevisaoConfirmada}
              isLoading={confirmandoRevisao}
            >
              Sim, documento revisado
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!pendingDocDialog}
        onClose={() => setPendingDocDialog(null)}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="xl" bg="gray.900">
          <ModalHeader fontSize="md" color="white">
            Documento pendente
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Flex align="center" gap={2}>
                <Icon as={WarningCircle} color="orange.400" boxSize={5} />
                <Text fontSize="sm" color="gray.300">
                  Este card já possui um documento pendente de assinatura.
                </Text>
              </Flex>
              <Text fontSize="sm" color="gray.400">
                O que deseja fazer?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} flexDirection="column" alignItems="stretch">
            <Button size="sm" colorScheme="blue" onClick={handlePendingMoveOnly}>
              Apenas mover card
            </Button>
            <Button size="sm" colorScheme="orange" variant="outline" onClick={handlePendingResend}>
              Cancelar documento e reenviar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="gray.400"
              onClick={() => setPendingDocDialog(null)}
            >
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal NF Emitida — Liquid Glass */}
      <Modal
        isOpen={!!pendingNfEmitida}
        onClose={() => setPendingNfEmitida(null)}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
        <ModalContent
          borderRadius="2xl"
          bg="rgba(255, 255, 255, 0.72)"
          backdropFilter="blur(40px) saturate(1.6)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.6)"
          boxShadow="0 8px 40px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="1px"
            bgGradient="linear(to-r, transparent, rgba(255,255,255,0.9), transparent)"
          />
          <ModalHeader fontSize="md" fontWeight="700" color="gray.800" pb={1}>
            Dados da Nota Fiscal
          </ModalHeader>
          <ModalCloseButton color="gray.400" _hover={{ color: 'gray.700' }} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.500">
                Informe os dados da NF emitida.
              </Text>
              <SimpleGrid columns={2} spacing={3}>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                    Numero da NF
                  </Text>
                  <Input
                    size="sm"
                    value={nfNumero}
                    onChange={e => setNfNumero(e.target.value)}
                    placeholder="Ex: 001234"
                    bg="rgba(255, 255, 255, 0.5)"
                    color="gray.800"
                    border="1px solid"
                    borderColor="rgba(0, 0, 0, 0.08)"
                    borderRadius="lg"
                    _hover={{ borderColor: 'rgba(0, 0, 0, 0.15)' }}
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.4)',
                      bg: 'rgba(255, 255, 255, 0.7)',
                    }}
                    _placeholder={{ color: 'gray.400' }}
                    autoFocus
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                    Valor da NF (R$)
                  </Text>
                  <Input
                    size="sm"
                    value={nfValor}
                    onChange={e => setNfValor(e.target.value)}
                    placeholder="Ex: 15.000,00"
                    bg="rgba(255, 255, 255, 0.5)"
                    color="gray.800"
                    border="1px solid"
                    borderColor="rgba(0, 0, 0, 0.08)"
                    borderRadius="lg"
                    _hover={{ borderColor: 'rgba(0, 0, 0, 0.15)' }}
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.4)',
                      bg: 'rgba(255, 255, 255, 0.7)',
                    }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </Box>
              </SimpleGrid>

              <Flex
                align="center"
                bg="rgba(0, 0, 0, 0.03)"
                border="1px solid"
                borderColor="rgba(0, 0, 0, 0.06)"
                borderRadius="lg"
                px={3}
                py={2}
              >
                <Text fontSize="sm" color="gray.700" flex={1}>
                  Tem retenção?
                </Text>
                <Switch
                  size="sm"
                  colorScheme="blue"
                  isChecked={nfTemRetencao}
                  onChange={e => setNfTemRetencao(e.target.checked)}
                />
              </Flex>

              {nfTemRetencao && (
                <Box
                  bg="rgba(0, 0, 0, 0.02)"
                  border="1px solid"
                  borderColor="rgba(0, 0, 0, 0.06)"
                  borderRadius="xl"
                  p={3}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.400"
                    mb={2}
                    letterSpacing="0.05em"
                  >
                    VALORES DE RETENÇÃO
                  </Text>
                  <SimpleGrid columns={3} spacing={2}>
                    {[
                      { key: 'iss', label: 'ISS' },
                      { key: 'ir', label: 'IR' },
                      { key: 'csll', label: 'CSLL' },
                      { key: 'cofins', label: 'COFINS' },
                      { key: 'pis', label: 'PIS' },
                      { key: 'inss', label: 'INSS' },
                    ].map(({ key, label }) => (
                      <Box key={key}>
                        <Text fontSize="10px" fontWeight="600" color="gray.400" mb={0.5}>
                          {label}
                        </Text>
                        <Input
                          size="xs"
                          value={nfRetencoes[key as keyof typeof nfRetencoes]}
                          onChange={e =>
                            setNfRetencoes(prev => ({ ...prev, [key]: e.target.value }))
                          }
                          placeholder="0,00"
                          bg="rgba(255, 255, 255, 0.5)"
                          color="gray.800"
                          border="1px solid"
                          borderColor="rgba(0, 0, 0, 0.06)"
                          borderRadius="md"
                          _hover={{ borderColor: 'rgba(0, 0, 0, 0.12)' }}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.3)',
                            bg: 'rgba(255, 255, 255, 0.7)',
                          }}
                          _placeholder={{ color: 'gray.300' }}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} borderTop="1px solid" borderColor="rgba(0, 0, 0, 0.05)">
            <Button
              size="sm"
              variant="ghost"
              color="gray.500"
              _hover={{ bg: 'rgba(0, 0, 0, 0.04)', color: 'gray.700' }}
              onClick={() => setPendingNfEmitida(null)}
              isDisabled={nfSending}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              bg="blue.500"
              color="white"
              _hover={{ bg: 'blue.600' }}
              _active={{ bg: 'blue.700' }}
              onClick={handleNfEmitidaConfirm}
              isLoading={nfSending}
              isDisabled={!nfNumero.trim()}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
